import { useEffect, useState } from "react";

import { faSpinner } from "@fortawesome/pro-regular-svg-icons";
import { shuffle } from "lodash-es";
import { AnimatePresence, m } from "motion/react";

import { Icon } from "src/design-system/Icon";

/**
 * Stands in whenever the turn names no action of its own. Plain words on
 * purpose: most readers of a copilot are not engineers, so none of these
 * names a mechanism.
 */
export const loadingTexts = [
  "Working on it...",
  "Thinking it through...",
  "Looking into it...",
  "Getting to work...",
  "Figuring it out...",
  "Taking a closer look...",
  "Reading your request...",
  "Putting it together...",
  "Working through the details...",
  "Writing it up...",
] as const;

/**
 * Kept in step with the agent execution log's thinking indicator, so both
 * surfaces advance their label at the same pace.
 */
const TEXT_ROTATION_INTERVAL_MS = 4500;

/** Counts elapsed rotation intervals since the indicator appeared. */
const useRotationStep = (): number => {
  const [step, setStep] = useState(0);

  useEffect(() => {
    const interval = setInterval(
      () => setStep((prev) => prev + 1),
      TEXT_ROTATION_INTERVAL_MS,
    );

    return () => clearInterval(interval);
  }, []);

  return step;
};

export const UnifiedCopilotLoadingIndicator: React.FC<{
  /**
   * The action the agent is taking right now, shown in place of the rotating
   * generic copy. Pass it whenever the caller knows more than "something is
   * happening".
   */
  text?: string;
  /**
   * What the agent is saying about that action, shown under it in a quieter
   * colour. Independent of `text`: the generic copy can carry a detail too.
   */
  detail?: string;
  isBooting?: boolean;
  dataLoc?: string;
}> = ({ text, detail, isBooting = false, dataLoc }) => {
  // Shuffled once per mount: cycling a shuffled pool keeps the first label as
  // random as it always was, and shows every other one before repeating.
  const [loadingOrder] = useState(() => shuffle(loadingTexts));
  const step = useRotationStep();
  const action =
    text ??
    (isBooting ? "Booting..." : loadingOrder[step % loadingOrder.length]);

  return (
    <li className="flex flex-col gap-y-2" data-loc={dataLoc}>
      {/* Top-aligned rather than centered: the icon's box and the action line
      are both 20px tall, so aligning their tops centers the spinner on that
      line whatever the detail below it does. */}
      <div className="flex items-start gap-x-0.5">
        <Icon icon={faSpinner} size="xs" spin />
        <div className="flex min-w-0 flex-col">
          <AnimatePresence mode="wait">
            <m.span
              key={action}
              animate={{ opacity: 1 }}
              className="font-inter-normal-13px text-gray-800"
              exit={{ opacity: 0 }}
              initial={{ opacity: 0 }}
              transition={{ duration: 0.15, ease: "easeInOut" }}
            >
              {action}
            </m.span>
          </AnimatePresence>
          {detail !== undefined && (
            // Unanimated: the detail is rewritten as the agent writes, and
            // fading it on every poll would read as flicker.
            <p className="line-clamp-3 font-inter-normal-12px text-gray-500">
              {detail}
            </p>
          )}
        </div>
      </div>
    </li>
  );
};
